import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { AlertTriangle } from 'lucide-react'

interface HelpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>连接帮助</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4 text-sm">
            <section>
              <h3 className="font-semibold mb-2">1. 浏览器要求</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li>✓ Chrome/Edge 61+ 或 Opera 48+</li>
                <li>✗ 不支持 Firefox、Safari</li>
                <li>• 必须使用 HTTPS（localhost 除外）</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold mb-2">2. 设备准备</h3>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>设置 → 关于手机 → 连续点击"版本号" 7 次</li>
                <li>返回设置 → 开发者选项 → 开启 <span className="text-foreground">USB 调试</span></li>
                <li>（推荐）开启 <span className="text-foreground">USB 调试（安全设置）</span></li>
              </ol>
            </section>

            <section>
              <h3 className="font-semibold mb-2">3. 连接设备</h3>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>关闭可能占用 ADB 的程序（Android Studio 等）</li>
                <li>执行 <code className="bg-muted px-1 rounded">adb kill-server</code> 关闭系统 ADB</li>
                <li>USB 数据线连接手机到电脑</li>
                <li>点击页面上的"连接设备"按钮</li>
                <li>在弹出窗口中选择你的设备</li>
                <li>手机上点击"允许 USB 调试"</li>
              </ol>
            </section>

            <section>
              <h3 className="font-semibold mb-2">常见问题</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li><span className="text-foreground">检测不到设备：</span>检查数据线是否支持数据传输、换 USB 口</li>
                <li><span className="text-foreground">无授权提示：</span>切换 USB 模式为"文件传输"、重启 USB 调试</li>
                <li><span className="text-foreground">连接后断开：</span>检查线材质量、安装官方驱动</li>
                <li><span className="text-foreground">权限不足：</span>开启"USB 调试（安全设置）"</li>
              </ul>
            </section>

            <section className="border rounded-lg p-3">
              <h3 className="font-semibold mb-2 text-destructive">设备已被其他程序占用</h3>
              <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground text-xs">
                <li>关闭占用设备的程序：Android Studio、VS Code、Chrome 远程调试器、模拟器等</li>
                <li>如果仍然失败，按 <kbd className="bg-muted px-1 rounded">Ctrl+Shift+ESC</kbd> 打开任务管理器</li>
                <li>在"进程"页面找到 <code className="bg-muted px-1 rounded">adb.exe</code></li>
                <li>右键点击 → 结束任务</li>
              </ol>
            </section>

            <section className="bg-muted/50 rounded-lg p-3">
              <h3 className="font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                安全提示
              </h3>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li>• 数据完全在浏览器本地处理，不会上传服务器</li>
                <li>• 仅在信任的网站上使用连接</li>
                <li>• 公共电脑上建议不勾选"总是允许"</li>
                <li>• 使用完毕后可关闭 USB 调试</li>
              </ul>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
